import { API_BASE_URL } from './api.js';
import { DomainApiError } from './api-error.js';

/**
 * Krok PROV1 - prehled provizi po maklerich a mesicni uzaverka nad
 * `/v1/finance/commission-*`.
 *
 * Raw `fetch` (ne generovany `@reos/api-client`): routy jeste nejsou
 * v `openapi.json`, ktery regeneruje CI - stejny vzor a stejny duvod jako
 * `acquisition-radar-api.ts` (AKV1) a `ai-credits-api.ts`. Odpovedi
 * financniho pluginu jsou `zod.any()`, DTO tvar proto drzi tyhle typy podle
 * `packages/finance/src/public/plugin.ts`.
 */

export type PayoutRecipientType = 'broker' | 'office' | 'agency';
export type CommissionPeriodStatus = 'open' | 'locked' | 'paid';

export interface CommissionPayoutRowDto {
  readonly month: string;
  readonly recipientType: PayoutRecipientType;
  readonly brokerUserId: string | null;
  readonly officeId: string | null;
  readonly label: string | null;
  readonly currency: string;
  readonly entitledAmount: string;
  readonly paidAmount: string;
  readonly remainingAmount: string;
  readonly periodStatus: CommissionPeriodStatus;
}

export interface CommissionPeriodDto {
  readonly month: string;
  readonly status: CommissionPeriodStatus;
  readonly lockedAt: string | null;
  readonly paidAt: string | null;
  readonly reopenReason: string | null;
  readonly reopenedAt: string | null;
}

export interface CommissionPayoutOverviewDto {
  readonly rows: readonly CommissionPayoutRowDto[];
  readonly periods: readonly CommissionPeriodDto[];
  /** `all` = majitel/finance, `office` = vedouci pobocky, `own` = makler. */
  readonly scope: 'all' | 'office' | 'own';
}

export interface PayoutsQuery {
  readonly monthFrom?: string;
  readonly monthTo?: string;
  readonly officeId?: string;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { detail?: string; title?: string; type?: string } | null;
    const text = [body?.title, body?.detail].filter((part): part is string => typeof part === 'string').join(' ');
    throw new DomainApiError(text === '' ? `HTTP ${String(res.status)}` : text, res.status, body?.type);
  }
  return (await res.json()) as T;
}

function queryString(query: PayoutsQuery, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams(extra);
  if (query.monthFrom !== undefined) params.set('monthFrom', query.monthFrom);
  if (query.monthTo !== undefined) params.set('monthTo', query.monthTo);
  if (query.officeId !== undefined) params.set('officeId', query.officeId);
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

export async function getCommissionPayouts(query: PayoutsQuery = {}): Promise<CommissionPayoutOverviewDto> {
  return req<CommissionPayoutOverviewDto>(`/v1/finance/commission-payouts${queryString(query)}`);
}

/** Odkaz na CSV vypis pro uctarnu (stejny vzor jako exporty tiskove sady AKV1). */
export function commissionPayoutsCsvUrl(query: PayoutsQuery = {}): string {
  return `${API_BASE_URL}/v1/finance/commission-payouts${queryString(query, { format: 'csv' })}`;
}

export async function lockCommissionPeriod(month: string): Promise<CommissionPeriodDto> {
  return req<CommissionPeriodDto>(`/v1/finance/commission-periods/${month}/lock`, { method: 'POST' });
}

export async function payoutCommissionPeriod(month: string): Promise<CommissionPeriodDto> {
  return req<CommissionPeriodDto>(`/v1/finance/commission-periods/${month}/payout`, { method: 'POST' });
}

export async function reopenCommissionPeriod(month: string, reason: string): Promise<CommissionPeriodDto> {
  return req<CommissionPeriodDto>(`/v1/finance/commission-periods/${month}/reopen`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export interface CommissionShareDto {
  readonly id: string;
  readonly caseId: string;
  readonly recipientType: PayoutRecipientType;
  readonly brokerUserId: string | null;
  readonly officeId: string | null;
  readonly label: string | null;
  readonly basisType: 'percent' | 'fixed';
  readonly basisValue: string;
  readonly sortOrder: number;
}

export interface CommissionShareInput {
  readonly recipientType: PayoutRecipientType;
  readonly brokerUserId: string | null;
  readonly officeId: string | null;
  readonly label: string | null;
  readonly basisType: 'percent' | 'fixed';
  readonly basisValue: string;
}

export async function listCaseCommissionShares(caseId: string): Promise<readonly CommissionShareDto[]> {
  const body = await req<{ items: readonly CommissionShareDto[] }>(`/v1/cases/${caseId}/commission-shares`);
  return body.items;
}

export async function setCaseCommissionShares(
  caseId: string,
  shares: readonly CommissionShareInput[],
): Promise<readonly CommissionShareDto[]> {
  const body = await req<{ items: readonly CommissionShareDto[] }>(`/v1/cases/${caseId}/commission-shares`, {
    method: 'PUT',
    body: JSON.stringify({ shares }),
  });
  return body.items;
}
